import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { profileQueryOptions } from './business';
import { firstIncompleteStep, isAssessmentComplete, ONBOARDING_PATH } from './onboarding';
import { useOnboardingStore } from './onboarding-store';
import { isStaffRole, sessionQueryOptions } from './session';
import { buildSubdomainUrl } from '../utils/subdomain';
import { staffSecurityQuery } from './workspace-api';

const ONBOARDING_PREFIX = '/subdomain/app/onboarding';

/** Staff never use the client portal: send them to the staff app on its own host. */
function toStaffApp(): never {
  throw redirect({ href: buildSubdomainUrl('admin', '/') });
}

const assessmentComplete = () => isAssessmentComplete(useOnboardingStore.getState().draft);

/** Where the assessment should resume: the first unanswered question (question 1 when nothing is saved). */
function assessmentStepPath() {
  return ONBOARDING_PATH[firstIncompleteStep(useOnboardingStore.getState().draft) ?? 'industry'];
}

/** The session as the server sees it right now (the cached copy can be up to a minute stale, or from before a sign-in). */
const freshSession = (queryClient: QueryClient) => queryClient.fetchQuery({ ...sessionQueryOptions, staleTime: 0 });

/**
 * A signed-in client with no profile yet. The assessment must be finished locally before anything is saved:
 * finished -> the audit screen saves it; unfinished -> resume the assessment where it stopped.
 */
function toFinishOnboarding(): never {
  if (assessmentComplete()) throw redirect({ to: '/subdomain/app/audit' });
  throw redirect({ to: assessmentStepPath() });
}

/**
 * Account creation (sign-up, then email verification) only makes sense after the assessment: the answers are what
 * the new account is created for. Opened directly, it sends the visitor back to the first unanswered question.
 */
export function requireAssessment() {
  if (!assessmentComplete()) throw redirect({ to: assessmentStepPath() });
}

/**
 * Pages for signed-out visitors (welcome, onboarding, sign-in...). A signed-in client has no business
 * here: back to the app, or - if they never finished onboarding - into the questionnaire.
 */
export async function redirectIfSignedIn(queryClient: QueryClient, pathname: string) {
  const session = await freshSession(queryClient);
  if (!session) return;
  // A staff session on the client sign-in pages is left alone (the page offers to sign in as someone else).
  // Redirecting it away made the client sign-in unreachable for anyone who had used the staff app in this browser.
  if (isStaffRole(session.user.role)) return;

  const profile = await queryClient.ensureQueryData(profileQueryOptions);
  if (profile) throw redirect({ to: '/subdomain/app' });
  if (!pathname.startsWith(ONBOARDING_PREFIX)) toFinishOnboarding();
}

/** The signed-in client area: needs a client session, and a saved business profile. */
export async function requireClient(queryClient: QueryClient) {
  const session = await queryClient.ensureQueryData(sessionQueryOptions);
  if (!session) throw redirect({ to: '/subdomain/app/welcome' });
  if (isStaffRole(session.user.role)) toStaffApp();

  const profile = await queryClient.ensureQueryData(profileQueryOptions);
  if (!profile) toFinishOnboarding();
  return session;
}

/**
 * The audit screen creates the business profile, so it needs both halves: an account (a live session) AND a finished
 * assessment saved locally. Missing the account -> create one (or do the assessment first); missing the assessment ->
 * do it. A profile that already exists means there is nothing to save.
 */
export async function requireAuditAccess(queryClient: QueryClient) {
  const session = await freshSession(queryClient);
  if (!session) throw redirect({ to: assessmentComplete() ? '/subdomain/app/sign-up' : assessmentStepPath() });
  if (isStaffRole(session.user.role)) toStaffApp();

  const profile = await queryClient.fetchQuery({ ...profileQueryOptions, staleTime: 0 });
  if (profile) throw redirect({ to: '/subdomain/app' });
  if (!assessmentComplete()) throw redirect({ to: assessmentStepPath() });
  return session;
}

/** Staff sign-in / invite pages: someone already signed in belongs in their own app. */
export async function redirectIfStaffSignedIn(queryClient: QueryClient) {
  const session = await queryClient.ensureQueryData(sessionQueryOptions);
  if (!session) return;
  if (isStaffRole(session.user.role)) throw redirect({ to: '/subdomain/admin' });
  // A client session on the staff sign-in page must not bounce the visitor away: they could then never reach the
  // form to sign in as staff. The page itself handles it (signing in replaces the session).
}

/** The staff app: needs a staff session. Clients are sent to the client portal. */
export async function requireStaff(queryClient: QueryClient) {
  const session = await queryClient.ensureQueryData(sessionQueryOptions);
  if (!session) throw redirect({ to: '/subdomain/admin/admin-login' });
  if (!isStaffRole(session.user.role)) throw redirect({ href: buildSubdomainUrl('app', '/') });
  return session;
}

/** Admin-only screens. Hide rather than disable (KT §7.1): other staff roles go back to their home. */
export async function requireAdmin(queryClient: QueryClient) {
  const session = await requireStaff(queryClient);
  if (session.user.role !== 'admin') throw redirect({ to: '/subdomain/admin' });
  return session;
}

/** The business directory: admins and account managers. Other staff roles go back to their home (KT §7.1: hide, don't disable). */
export async function requireBusinessAccess(queryClient: QueryClient) {
  const session = await requireStaff(queryClient);
  if (session.user.role !== 'admin' && session.user.role !== 'account_manager')
    throw redirect({ to: '/subdomain/admin' });
  return session;
}

/**
 * Once the workspace's grace period for the required two-step is over, a staff member who still has not set it up
 * can only reach Security (where they turn it on). The server enforces the same rule on its staff APIs.
 * If the requirement cannot be read, they are let through: the server is the one that actually blocks.
 */
export async function requireTwoFactorSetup(queryClient: QueryClient, pathname: string) {
  // No retries: a failing read must not hold up every staff page load.
  const security = await queryClient.fetchQuery({ ...staffSecurityQuery, retry: false }).catch(() => null);
  if (security?.mustSetUpNow && !pathname.endsWith('/settings/security')) {
    throw redirect({ to: '/subdomain/admin/settings/security' });
  }
}
